import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Form, FormArray, FormBuilder, FormGroup, UntypedFormArray } from '@angular/forms';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Contract } from '../../model/contract';
import { BlockContract } from '../../model/block-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractFormCommandHandlerResolver } from '../../service/command/contract-form-command-handler-resolver.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { PrintContractTerminationFormCommandHandler } from '../../service/command/print-contract-termination-form-command-handler';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { BlockContractService } from '../../service/data/block-contract.service';
import { ContractFormComponentBase } from '../base/contract-form.base';
import { ActivatedRoute } from '@angular/router';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { standardCommand, Command } from '../../../core/model/command';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { LeaseAgreementCategory, LeaseAgreementSubCategoryKind, RentalObjectCategory } from '../../model/const/enums';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { switchMap, tap } from 'rxjs/operators';
import { VacantRentalObjectService } from '../../service/data/vacant-period.service';
import { VacantRentalObject } from '../../model/vacant-rental-object';
import { LocalDate } from '../../../core/model/local-date';
import { BlockRentalObject } from '../../model/block-rental-object';
import { of, Subject } from 'rxjs';
import { Company } from '../../model/company';
import { LeaseAgreementRentalObject } from '../../model/lease-agreement-rental-object';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { LeaseAgreementLogService } from '../../service/data/lease-agreement-log.service';
import { LeaseRow } from '../../model/lease-row';
import { LeaseAgreementImdConsumptionService } from '../../service/data/lease-agreement-imd-consumption.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-block-contract-form',
    templateUrl: './block-contract-form.component.html',
    styleUrls: ['./block-contract-form.component.scss'],
    providers: [
        ApproveContractCommandHandler,
        ContractFormCommandHandlerResolver,
        DeleteFormCommandHandler,
        PrintContractTerminationFormCommandHandler,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        { provide: ContractService, useExisting: BlockContractService },
        { provide: DataService, useExisting: BlockContractService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BlockContractFormComponent extends ContractFormComponentBase<Contract> {
  leaseAgreementDocumentCount = 0;
  constructor(
    activatedRoute: ActivatedRoute,
    private _dataService: BlockContractService,
    dispatcher: CommandDispatcher,
    cdf: ColumnDefinitionFactory,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    fcf: FormControlExFactory,
    handlerResolver: ContractFormCommandHandlerResolver,
    messageService: MessageService,
    messageDispatcher: ComponentMessageDispatcher,
    navigation: NavigationService,
    userAccount: UserAccountService,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _vacancyService: VacantRentalObjectService,
    logService: LeaseAgreementLogService,
    leaseAgreementImdConsumptionService: LeaseAgreementImdConsumptionService,
    languageService: LanguageService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super(
      messageDispatcher,
      cdf,
      fb,
      fcf,
      userAccount,
      cds,
      activatedRoute,
      _dataService,
      messageService,
      navigation,
      dispatcher,
      handlerResolver,
      logService,
      leaseAgreementImdConsumptionService,
      languageService
    );

    this.AddSubscription(
      this._objectSelectionStartDateChanged
        .pipe(
          switchMap(fromDate => {
            const propertyId = this.filterForm?.controls['propertyId'].value ?? this.dataContext?.propertyId;
            return propertyId
              ? this._dataService.getObjectSelection(propertyId, this.dataContext.id, fromDate)
              : of(null);
          })
        )
        .subscribe(result => {
          if (result) {
            this.setObjectSelection(result.dwellings, result.otherObjects, result.parkings, result.premises);
          }
        })
    );

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
        },
      })
    );
  }

  onDocumentsCountChanged(count: number) {
    this.leaseAgreementDocumentCount = count;
    this._cdr.markForCheck();
  }

  categories: SelectionItem[] = [
    new SelectionItem('CONST.RENTAL_OBJECT_CATEGORY_DWELLING', RentalObjectCategory.Dwelling),
    new SelectionItem('CONST.RENTAL_OBJECT_CATEGORY_PARKING', RentalObjectCategory.Parking),
    new SelectionItem('CONST.RENTAL_OBJECT_CATEGORY_PREMISES', RentalObjectCategory.Premises),
    new SelectionItem('CONST.RENTAL_OBJECT_CATEGORY_OTHER', RentalObjectCategory.Other),
  ];

  subCategories: SelectionItem[] = [
    new SelectionItem('CONST.LEASE_AGREEMENT_SUB_CATEGORY_BLOCK', LeaseAgreementSubCategoryKind.None),
    new SelectionItem('CONST.LEASE_AGREEMENT_SUB_CATEGORY_PREMISES', LeaseAgreementSubCategoryKind.BlockLeaseAgreementPremises),
  ];

  onRentalObjectClicked(form: FormGroup): void {
    if (form.controls['rentalObjectId'].value) {
      this.navigation.navigate([
        this.getBlockContractRentalObjectPath(form.controls['category'].value),
        'edit',
        form.controls['rentalObjectId'].value,
      ]);
    }
  }

  private _fixedProperty: string;
  private _fixedCompany: string;
  private readonly _objectSelectionStartDateChanged = new Subject<LocalDate>();

  protected onMessageRecieved(message: any) {
    if (Array.isArray(message)) {
      for (let item of message) {
        this._fixedCompany = item.company;
        this._fixedProperty = item.property;
        let vacantRentalObject = item as VacantRentalObject;
        let form = this.addRentalObject();
        form.controls['category'].setValue(vacantRentalObject.category);
        form.controls['rentalObjectId'].setValue(vacantRentalObject.rentalObjectId);
        let vacantFrom = LocalDate.fromDate(vacantRentalObject.vacantFrom);
        if (vacantFrom.hasValue) {
          let fromDate = LocalDate.fromDate(
            vacantFrom.toDate() <= LocalDate.today().toDate()
              ? vacantFrom
              : LocalDate.fromDate(LocalDate.today().firstDayInMonth()).addMonths(1)
          );
          form.controls['fromDate'].setValue(fromDate);
        }
        form.controls['toDate'].setValue(vacantRentalObject.vacantTo);
      }
      this.updateRentalObjectSelection();

      this.messageDispatcher.messageDelivered();
    }
  }

  get hasSecurity(): boolean {
    const hasDeposit =
      this.dataContext?.depositAmount?.amount > 0 ||
      (this.dataContext?.depositComment && this.dataContext.depositComment.trim().length > 0);
    const hasGuarantor = this.dataContext?.securities && this.dataContext.securities.length > 0;
    return hasDeposit || hasGuarantor;
  }

  protected addRentalObjectFormData(form: FormGroup): void {
    let formArray = new UntypedFormArray([]);
    let rentalObjects = this.dataContext.rentalObjects ? this.dataContext.rentalObjects : [];
    for (let rentalObject of rentalObjects) {
      formArray.push(this.createRentalObjectForm(rentalObject));
    }
    form.addControl('rentalObjects', formArray);
    this.rentalObjectForms = formArray;
    this.updateRentalObjectSelection();
  }

  addRentalObject(): FormGroup {
    let form = this.createRentalObjectForm({});
    this.rentalObjectForms.push(form);
    this.rentalObjectForms.markAsDirty();
    this.form.markAsDirty();
    return form;
  }

  removeRentalObject(index: number) {
    let form = this.rentalObjectForms.at(index) as FormGroup;
    let rentalObjectId = form.controls['rentalObjectId'].value;
    this.rentalObjectForms.removeAt(index);
    this.form.markAsDirty();
    if (rentalObjectId) {
      this.updateRentalObjectSelection();
    }
  }

  rentalObjectForms = new FormArray([]);

  createRentalObjectForm(rentalObject: any): FormGroup {
    let group = this.fb.group({
      category: this.fcf.createMandatory(
        'COMMON.RENTAL_OBJECT_CATEGORY',
        rentalObject && rentalObject.category ? rentalObject.category : RentalObjectCategory.Dwelling
      ),
      rentalObjectId: this.fcf.createMandatory('LEASE_AGREEMENT.RENTAL_OBJECT', rentalObject.rentalObjectId),
      fromDate: this.fcf.create('LEASE_AGREEMENT.OBJECT_INCLUDED_FROM', rentalObject.fromDate),
      toDate: this.fcf.create('LEASE_AGREEMENT.OBJECT_INCLUDED_TO', rentalObject.toDate),
    });

    this.AddSubscription(
      group.controls['category'].valueChanges.subscribe(v => {
        if (!this._fixedProperty) {
          group.controls['rentalObjectId'].setValue(null);
        }
      })
    );
    this.AddSubscription(
      group.controls['rentalObjectId'].valueChanges.subscribe(v => {
        this.updateRentalObjectSelection();
      })
    );

    return group;
  }

  private updateRentalObjectSelection(): void {
    let rentalObjectSelection = [new SelectionItem('COMMON.ALL_NONE', null)];
    if (
      this.rentalObjectForms.controls.length > 0 &&
      this.dwellingSelection &&
      this.otherObjectsSelection &&
      this.parkingSelection &&
      this.premisesSelection
    ) {
      let rentalObjectIds = this.rentalObjectForms.controls
        .map(c => c as FormGroup)
        .map(g => g.controls['rentalObjectId'].value)
        .filter(id => id);
      this.dwellingSelection.filter(r => rentalObjectIds.find(id => id === r.value)).forEach(r => rentalObjectSelection.push(r));
      this.otherObjectsSelection.filter(r => rentalObjectIds.find(id => id === r.value)).forEach(r => rentalObjectSelection.push(r));
      this.parkingSelection.filter(r => rentalObjectIds.find(id => id === r.value)).forEach(r => rentalObjectSelection.push(r));
      this.premisesSelection.filter(r => rentalObjectIds.find(id => id === r.value)).forEach(r => rentalObjectSelection.push(r));
    }
    this.rentalObjectSelection = rentalObjectSelection.sort(this.sortSelection);
  }

  private sortSelection(a, b): number {
    if (a && !b) return 1;
    if (!a && b) return -1;
    if (!a && !b) return 0;
    if (a.name > b.name) return 1;
    if (a.name < b.name) return -1;

    return 0;
  }

  getObjectSelection(objectForm: FormGroup): SelectionItem[] {
    const selection = this.getObjectSelectionByCategory(objectForm.controls['category'].value);
    const selectedId = objectForm.controls['rentalObjectId'].value;
    const objectsSelectedOnOtherRows = new Set(
      this.rentalObjectForms.controls
        .filter(control => control !== objectForm)
        .map(control => control as FormGroup)
        .map(group => group.controls['rentalObjectId'].value)
        .filter(id => id)
    );

    return selection.filter(item => item.value === selectedId || !objectsSelectedOnOtherRows.has(item.value));
  }

  private getObjectSelectionByCategory(category: RentalObjectCategory): SelectionItem[] {
    switch (category) {
      case RentalObjectCategory.Dwelling:
        return this.dwellingSelection;
      case RentalObjectCategory.Other:
        return this.otherObjectsSelection;
      case RentalObjectCategory.Parking:
        return this.parkingSelection;
      case RentalObjectCategory.Premises:
        return this.premisesSelection;
      default:
        return [];
    }
  }

  private clearRentalObjects() {
    while (this.rentalObjectForms.controls.length > 0) {
      this.removeRentalObject(0);
    }
    this.addRentalObject();
  }

  init(dataContext: Contract) {
    super.init(dataContext);
    this.filterForm = this.fb.group({
      companyId: this.fcf.createMandatory('COMMON.COMPANY', null),
      propertyId: this.fcf.createMandatory('COMMON.PROPERTY', null),
    });

    if (this.isNew) {
      this.form.controls['subCategory'].setValue(LeaseAgreementSubCategoryKind.None);

      this.AddSubscription(
        this.filterForm.controls['companyId'].valueChanges
          .pipe(
            switchMap(val => {
              let company = this.companies.find(c => c.id === val);
              if (company) {
                this.dataContext.currencyCode = company.currencyCode;
              }
              return this._propertyService.getSelectionItems({ company: val }, false);
            })
          )
          .subscribe(properties => {
            this.propertySelection = properties;
            let selectedId = this._fixedProperty ? this._fixedProperty : this.filterForm.controls['propertyId'].value;
            let selected = properties.find(p => p.value === selectedId);
            if (!selected) {
              selected = properties.length == 0 ? null : properties[0];
            }
            this.filterForm.controls['propertyId'].setValue(selected ? selected.value : null);
          })
      );

      this.AddSubscription(
        this.filterForm.controls['propertyId'].valueChanges
          .pipe(
            switchMap(val =>
              val ? this._dataService.getObjectSelection(val, this.dataContext.id, this.getObjectSelectionStartDate()) : of(null)
            )
          )
          .subscribe(result => {
            if (result) {
              this.setObjectSelection(result.dwellings, result.otherObjects, result.parkings, result.premises);
              if (!this._fixedProperty) {
                this.clearRentalObjects();
              }
            }
          })
      );

      this.AddSubscription(
        this._companyService.getList().subscribe(companies => {
          this.companySelection = companies.map(c => new SelectionItem(`${c.number} ${c.name}`, c.id));
          this.companies = companies;
          this.filterForm.controls['companyId'].setValue(
            this._fixedCompany ? this._fixedCompany : this.dataContext.company ? this.dataContext.company : companies[0].id
          );
        })
      );
      if (!this._fixedProperty) {
        this.addRentalObject();
      }
    } else {
      this.AddSubscription(
        this._dataService
          .getObjectSelection(this.dataContext.propertyId, this.dataContext.id, this.getObjectSelectionStartDate())
          .subscribe(result => {
            this.setObjectSelection(result.dwellings, result.otherObjects, result.parkings, result.premises);
          })
      );
    }
  }

  onNoticePeriodFromChanged(value: LocalDate) {
    const fromDate = LocalDate.fromDate(value);
    super.onNoticePeriodFromChanged(fromDate);

    const propertyId = this.filterForm?.controls['propertyId'].value ?? this.dataContext?.propertyId;
    if (!propertyId) return;

    this._objectSelectionStartDateChanged.next(fromDate);
  }

  private getObjectSelectionStartDate(): LocalDate {
    const fromDate = LocalDate.fromDate(this.fromDate);
    return fromDate?.hasValue() ? fromDate : LocalDate.fromDate(this.dataContext?.noticePeriod?.from);
  }

  private setObjectSelection(
    dwellings: BlockRentalObject[],
    otherObjects: BlockRentalObject[],
    parkings: BlockRentalObject[],
    premises: BlockRentalObject[]
  ): void {
    let rentalObjects = [];
    dwellings.forEach(r => rentalObjects.push(r));
    otherObjects.forEach(r => rentalObjects.push(r));
    parkings.forEach(r => rentalObjects.push(r));
    premises.forEach(r => rentalObjects.push(r));
    this.rentalObjects = rentalObjects;

    this.dwellingSelection = dwellings.map(d => new SelectionItem(d.toString(), d.id)).sort(this.sortSelection);
    this.otherObjectsSelection = otherObjects.map(d => new SelectionItem(d.toString(), d.id)).sort(this.sortSelection);
    this.parkingSelection = parkings.map(d => new SelectionItem(d.toString(), d.id)).sort(this.sortSelection);
    this.premisesSelection = premises.map(d => new SelectionItem(d.toString(), d.id)).sort(this.sortSelection);

    this.clearUnavailableRentalObjectSelections();
    this.updateRentalObjectSelection();
  }

  private clearUnavailableRentalObjectSelections(): void {
    this.rentalObjectForms.controls
      .map(control => control as FormGroup)
      .forEach(objectForm => {
        const rentalObjectIdControl = objectForm.controls['rentalObjectId'];
        const selectedId = rentalObjectIdControl.value;
        if (!selectedId) return;

        const selection = this.getObjectSelectionByCategory(objectForm.controls['category'].value);
        if (!selection.some(item => item.value === selectedId)) {
          rentalObjectIdControl.setValue(null, { emitEvent: false });
        }
      });
  }

  filterForm: FormGroup;
  rentalObjects: BlockRentalObject[];
  rentalObjectSelection: SelectionItem[];
  companies: Company[] = [];
  companySelection: SelectionItem[] = [];
  propertySelection: SelectionItem[] = [];
  dwellingSelection: SelectionItem[] = [];
  otherObjectsSelection: SelectionItem[] = [];
  parkingSelection: SelectionItem[] = [];
  premisesSelection: SelectionItem[] = [];

  protected createModel(): Contract {
    let model = new BlockContract();
    model.invoiceAddress.country = '';
    return model;
  }

  protected prepareSave(): Contract {
    let model = super.prepareSave();
    model.rentalObjects = this.rentalObjectForms.value.map(item => new LeaseAgreementRentalObject(item));
    return model;
  }

  linkDocument() {
    const cmd = new Command<Contract>(leaseAgreementCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }
}
